import { computed, ref } from 'vue'
import { defineStore } from 'pinia'

import {
    applyBetaTemplate,
    createBetaAdvertisement,
    createBetaDomain,
    createBetaFile,
    createBetaSocialLink,
    createBetaLinkBuildingLink,
    createBetaPage,
    createBetaPost,
    deleteBetaPage,
    deleteBetaAdvertisement,
    deleteBetaDomain,
    deleteBetaFile,
    deleteBetaLinkBuildingLink,
    deleteBetaSocialLink,
    disconnectBetaIntegrationProfile,
    downloadBetaOpportunitiesCsv,
    duplicateBetaPage,
    fetchBetaActiveLayout,
    fetchBetaAccessCount,
    fetchBetaAdvertisements,
    fetchBetaDomainStatus,
    fetchBetaDomains,
    fetchBetaFiles,
    fetchBetaConnectedProfiles,
    fetchBetaIntegrationCodes,
    fetchBetaLinkBuilding,
    fetchBetaOpportunities,
    fetchBetaOpportunitiesCount,
    fetchBetaPageDocument,
    fetchBetaPages,
    fetchBetaPosts,
    fetchBetaReport,
    fetchBetaRedirects,
    fetchBetaSocialLinks,
    fetchBetaSiteAnalysis,
    fetchBetaSubscriptions,
    fetchBetaTemplates,
    openBetaStore,
    connectBetaIntegrationProfile,
    findBetaIntegrationProfile,
    setBetaMainDomain,
    saveBetaRedirects,
    sortBetaLinkBuildingLinks,
    sortBetaPages,
    updateBetaAdvertisement,
    updateBetaAdvertisementTitles,
    updateBetaLinkBuildingLink,
    updateBetaLinkBuildingPage,
    updateBetaPage,
    updateBetaIntegrationCodes,
    updateBetaSocialLink,
    uploadBetaFile,
    undoBetaTemplate,
} from '@/services/beta-dashboard'
import { useSiteStore } from '@/stores/site'
import notification from '@/services/notification'
import type {
    BetaAdvertisement,
    BetaAccessCount,
    BetaAccessPeriod,
    BetaAdvertisementInput,
    BetaAdvertisementTitles,
    BetaActiveLayout,
    BetaCreatePageInput,
    BetaDashboardTask,
    BetaDomain,
    BetaFile,
    BetaFileInput,
    BetaIntegrationCodes,
    BetaLayoutRollback,
    BetaLinkBuildingLink,
    BetaLinkBuildingLinkInput,
    BetaLinkBuildingPage,
    BetaOpportunity,
    BetaPage,
    BetaPageAssessment,
    BetaPagePreview,
    BetaPost,
    BetaProfileIntegration,
    BetaProfileIntegrationProvider,
    BetaReadinessStatus,
    BetaReport,
    BetaRedirect,
    BetaSiteAnalysis,
    BetaSocialLink,
    BetaSocialLinkInput,
    BetaSubscriptionsData,
    BetaTemplate,
} from '@/types/beta-dashboard'
import { calculateBetaProgress } from '@/utils/beta-progress'
import { deriveBetaPagePreview } from '@/utils/beta-page-preview'
import {
    assessBetaPage,
    buildBetaReadinessTasks,
} from '@/utils/beta-readiness'

export const useBetaDashboardStore = defineStore('beta-dashboard', () => {
    const siteStore = useSiteStore()

    const pages = ref<BetaPage[]>([])
    const reorderingPages = ref(false)
    const domains = ref<BetaDomain[]>([])
    const domainDnsTarget = ref('')
    const checkingDomainIds = ref<number[]>([])
    const domainStatusErrors = ref<Record<number, string>>({})
    const report = ref<BetaReport | null>(null)
    const accessCount = ref<BetaAccessCount | null>(null)
    const accessPeriod = ref<BetaAccessPeriod>(30)
    const accessPage = ref(1)
    const postsByPage = ref<Record<number, BetaPost[]>>({})
    const recentPosts = ref<BetaPost[]>([])
    const pageDocuments = ref<Record<number, unknown>>({})
    const documentErrors = ref<Record<number, string>>({})
    const templates = ref<BetaTemplate[]>([])
    const activeLayout = ref<BetaActiveLayout | null>(null)
    const layoutRollback = ref<BetaLayoutRollback | null>(null)
    const analysis = ref<BetaSiteAnalysis | null>(null)
    const analysisConsent = ref(false)
    const advertisements = ref<BetaAdvertisement[]>([])
    const advertisementTitles = ref<BetaAdvertisementTitles>({
        after_content: '',
        footer: '',
        sidebar: '',
        menu: '',
    })
    const linkBuildingPage = ref<BetaLinkBuildingPage | null>(null)
    const linkBuildingLinks = ref<BetaLinkBuildingLink[]>([])
    const redirects = ref<BetaRedirect[]>([])
    const files = ref<BetaFile[]>([])
    const integrationCodes = ref<BetaIntegrationCodes>({
        pixelId: '',
        analyticsId: '',
        analyticsIdV4: '',
        vimeoToken: '',
    })
    const connectedProfiles = ref<Record<BetaProfileIntegrationProvider, BetaProfileIntegration | null>>({
        insta: null,
        youtube: null,
    })
    const socialLinks = ref<BetaSocialLink[]>([])
    const opportunities = ref<BetaOpportunity[]>([])
    const opportunitiesCount = ref(0)
    const subscriptions = ref<BetaSubscriptionsData>({
        contracts: [],
        bills: [],
        payMethod: null,
    })

    const loading = ref(false)
    const loadingContent = ref(false)
    const loadingDomains = ref(false)
    const loadingReport = ref(false)
    const loadingAccessCount = ref(false)
    const loadingRecentPosts = ref(false)
    const loadingStore = ref(false)
    const loadingLayouts = ref(false)
    const applyingLayout = ref(false)
    const loadingAnalysis = ref(false)
    const loadingAdvertisements = ref(false)
    const loadingLinkBuilding = ref(false)
    const loadingRedirects = ref(false)
    const loadingFiles = ref(false)
    const loadingIntegrations = ref(false)
    const loadingOpportunities = ref(false)
    const loadingOpportunitiesCount = ref(false)
    const loadingSubscriptions = ref(false)
    const error = ref<string | null>(null)
    const reportError = ref<string | null>(null)
    const accessCountError = ref<string | null>(null)
    const recentPostsError = ref<string | null>(null)
    const domainsError = ref<string | null>(null)
    const layoutsError = ref<string | null>(null)
    const analysisError = ref<string | null>(null)
    const advertisementsError = ref<string | null>(null)
    const linkBuildingError = ref<string | null>(null)
    const redirectsError = ref<string | null>(null)
    const filesError = ref<string | null>(null)
    const integrationsError = ref<string | null>(null)
    const opportunitiesError = ref<string | null>(null)
    const opportunitiesCountError = ref<string | null>(null)
    const subscriptionsError = ref<string | null>(null)

    const contentLoaded = ref(false)
    const portfolioHasContent = ref<boolean | null>(null)

    const storageKey = computed(() => `uli:beta-dashboard:${siteStore.id}:progress:v1`)
    const layoutRollbackKey = computed(() => `uli:beta-dashboard:${siteStore.id}:layout-rollback:v2`)
    const analysisConsentKey = computed(() => `uli:beta-dashboard:${siteStore.id}:analysis-consent:v1`)
    const manualState = ref<{ reviewed?: boolean; domainDismissed?: boolean }>({})

    const loadManualState = () => {
        try {
            manualState.value = JSON.parse(localStorage.getItem(storageKey.value) || '{}')
        } catch {
            manualState.value = {}
        }
    }

    const persistManualState = () => {
        localStorage.setItem(storageKey.value, JSON.stringify(manualState.value))
    }

    const loadLayoutRollback = () => {
        try {
            const saved = sessionStorage.getItem(layoutRollbackKey.value)
            const parsed = saved ? JSON.parse(saved) : null
            layoutRollback.value = parsed
                && typeof parsed.token === 'string'
                && parsed.token
                && Number.isFinite(Number(parsed.templateId))
                && Number.isFinite(Number(parsed.appliedLayoutId))
                ? {
                    token: parsed.token,
                    templateId: Number(parsed.templateId),
                    templateName: String(parsed.templateName || 'Layout anterior'),
                    appliedLayoutId: Number(parsed.appliedLayoutId),
                }
                : null
        } catch {
            layoutRollback.value = null
        }
    }

    const persistLayoutRollback = (rollback: BetaLayoutRollback | null) => {
        layoutRollback.value = rollback

        try {
            if (rollback) {
                sessionStorage.setItem(layoutRollbackKey.value, JSON.stringify(rollback))
            } else {
                sessionStorage.removeItem(layoutRollbackKey.value)
            }
        } catch {
            // A troca continua disponível mesmo se o navegador bloquear sessionStorage.
        }
    }

    const loadAnalysisConsent = () => {
        try {
            analysisConsent.value = localStorage.getItem(analysisConsentKey.value) === '1'
        } catch {
            analysisConsent.value = false
        }
    }

    const persistAnalysisConsent = () => {
        analysisConsent.value = true

        try {
            localStorage.setItem(analysisConsentKey.value, '1')
        } catch {
            // O consentimento permanece válido durante a sessão atual.
        }
    }

    const listPages = computed(() => pages.value.filter((page) => page.listPages == 1))
    const contentPages = computed(() => pages.value.filter((page) => page.listPages != 1))
    const homePage = computed(() => (
        pages.value.find((page) => page.isHome)
        ?? pages.value.find((page) => page.id == siteStore.homePage)
        ?? null
    ))
    const pageAssessments = computed<BetaPageAssessment[]>(() => (
        contentPages.value
            .filter((page) => Object.prototype.hasOwnProperty.call(pageDocuments.value, page.id))
            .map((page) => assessBetaPage(page, pageDocuments.value[page.id]))
    ))
    const pagePreviews = computed<Record<number, BetaPagePreview>>(() => (
        Object.fromEntries(pages.value.map((page) => [
            page.id,
            deriveBetaPagePreview(
                {
                    ...page,
                    isHome: page.isHome || page.id == homePage.value?.id,
                },
                pageDocuments.value[page.id],
                postsByPage.value[page.id] ?? [],
            ),
        ]))
    ))
    const homeAssessment = computed(() => (
        pageAssessments.value.find((assessment) => (
            assessment.pageId === homePage.value?.id
            || assessment.kind === 'home'
        )) ?? null
    ))
    const aboutAssessment = computed(() => (
        pageAssessments.value.find((assessment) => assessment.kind === 'about') ?? null
    ))
    const aboutPage = computed(() => (
        aboutAssessment.value
            ? pages.value.find((page) => page.id === aboutAssessment.value?.pageId) ?? null
            : null
    ))
    const contentHasErrors = computed(() => Object.keys(documentErrors.value).length > 0)

    const identityReady = computed(() => Boolean(
        siteStore.name
        && (siteStore.logo || siteStore.logoMobile || siteStore.avatar),
    ))
    const contactReady = computed(() => Boolean(
        siteStore.contactEmail
        || siteStore.mainEmail
        || siteStore.contactPhone
        || siteStore.whatsapp,
    ))
    const customDomainReady = computed(() => domains.value.some((domain) => domain.automatic == 0))

    const statusFromAssessment = (
        assessment: BetaPageAssessment | null,
        field: 'hasMedia' | 'hasText',
        missingPageStatus: BetaReadinessStatus = 'unsupported',
    ): BetaReadinessStatus => {
        if (!contentLoaded.value) return 'unknown'
        if (!assessment) return missingPageStatus
        if (documentErrors.value[assessment.pageId]) return 'unknown'

        return assessment[field] ? 'complete' : 'pending'
    }

    const homeMediaStatus = computed<BetaReadinessStatus>(() => {
        if (homePage.value?.id && documentErrors.value[homePage.value.id]) return 'unknown'
        return statusFromAssessment(homeAssessment.value, 'hasMedia')
    })
    const homeCopyStatus = computed<BetaReadinessStatus>(() => {
        if (homePage.value?.id && documentErrors.value[homePage.value.id]) return 'unknown'
        return statusFromAssessment(homeAssessment.value, 'hasText')
    })
    const aboutStatus = computed<BetaReadinessStatus | 'not-applicable'>(() => {
        if (!contentLoaded.value) return 'unknown'
        if (!aboutAssessment.value) return 'not-applicable'

        return aboutAssessment.value.hasMedia && aboutAssessment.value.hasText
            ? 'complete'
            : 'pending'
    })
    const portfolioStatus = computed<BetaReadinessStatus | 'not-applicable'>(() => {
        if (!contentLoaded.value) return 'unknown'
        if (listPages.value.length === 0) return 'not-applicable'
        if (portfolioHasContent.value === null) return 'unknown'

        return portfolioHasContent.value ? 'complete' : 'pending'
    })

    const tasks = computed<BetaDashboardTask[]>(() => buildBetaReadinessTasks({
        identityReady: identityReady.value,
        contactReady: contactReady.value,
        contentLoaded: contentLoaded.value,
        contentHasErrors: contentHasErrors.value,
        homeMediaStatus: homeMediaStatus.value,
        homeCopyStatus: homeCopyStatus.value,
        aboutStatus: aboutStatus.value,
        portfolioStatus: portfolioStatus.value,
        reviewed: Boolean(manualState.value.reviewed),
        customDomainReady: customDomainReady.value,
        domainDismissed: Boolean(manualState.value.domainDismissed),
    }))

    const progress = computed(() => calculateBetaProgress(tasks.value))
    const nextTask = computed(() => (
        tasks.value.find((task) => task.mandatory && task.state === 'pending')
        ?? tasks.value.find((task) => task.mandatory && task.state === 'unknown')
        ?? tasks.value.find((task) => task.mandatory && task.state === 'unsupported')
        ?? null
    ))

    const loadNavigationPages = async () => {
        if (pages.value.length > 0) return pages.value

        loadingContent.value = true

        try {
            pages.value = await fetchBetaPages(Number(siteStore.id))
            return pages.value
        } catch (caughtError) {
            error.value = caughtError instanceof Error
                ? caughtError.message
                : 'Não foi possível carregar as páginas.'
            return []
        } finally {
            loadingContent.value = false
        }
    }

    const loadPagesAndProgress = async () => {
        loadingContent.value = true
        contentLoaded.value = false
        error.value = null
        pages.value = []
        postsByPage.value = {}
        pageDocuments.value = {}
        documentErrors.value = {}
        portfolioHasContent.value = null

        try {
            pages.value = await fetchBetaPages(Number(siteStore.id))

            const documentPages = contentPages.value.filter((page) => (
                page.type !== 'link'
                && page.pageType !== 'link'
            ))
            const documentResults = await Promise.allSettled(
                documentPages.map((page) => (
                    fetchBetaPageDocument(Number(siteStore.id), Number(page.id))
                )),
            )

            documentResults.forEach((result, index) => {
                const page = documentPages[index]

                if (result.status === 'fulfilled') {
                    pageDocuments.value[page.id] = result.value
                    return
                }

                documentErrors.value[page.id] = result.reason instanceof Error
                    ? result.reason.message
                    : 'Não foi possível verificar esta página.'
            })

            const listResults = await Promise.allSettled(
                listPages.value.map(async (page) => {
                    const posts = await fetchBetaPosts(Number(siteStore.id), page.id)
                    postsByPage.value[page.id] = posts
                    return posts
                }),
            )

            const fulfilledLists = listResults
                .filter((result): result is PromiseFulfilledResult<BetaPost[]> => (
                    result.status === 'fulfilled'
                ))
                .map((result) => result.value)
            const hasPublishedContent = fulfilledLists.some((posts) => (
                posts.some((post) => post.sketch != 1 && post.visible !== 0)
            ))
            const listLoadFailed = listResults.some((result) => result.status === 'rejected')

            if (hasPublishedContent) {
                portfolioHasContent.value = true
            } else if (listLoadFailed) {
                portfolioHasContent.value = null
            } else {
                portfolioHasContent.value = false
            }

            if (listLoadFailed) {
                error.value = 'Algumas publicações não puderam ser verificadas.'
            }

            if (contentHasErrors.value) {
                error.value = 'Algumas páginas não puderam ser verificadas.'
            }

            contentLoaded.value = true
        } catch (caughtError) {
            error.value = caughtError instanceof Error
                ? caughtError.message
                : 'Não foi possível carregar o conteúdo.'
            portfolioHasContent.value = null
        } finally {
            loadingContent.value = false
        }
    }

    const checkDomain = async (domainId: number) => {
        if (checkingDomainIds.value.includes(domainId)) return

        checkingDomainIds.value = [...checkingDomainIds.value, domainId]
        const remainingErrors = { ...domainStatusErrors.value }
        delete remainingErrors[domainId]
        domainStatusErrors.value = remainingErrors

        try {
            const result = await fetchBetaDomainStatus(Number(siteStore.id), domainId)
            if (result.target) domainDnsTarget.value = result.target

            domains.value = domains.value.map((domain) => (
                domain.id === domainId
                    ? {
                        ...domain,
                        status: result.status,
                        dnsConfigured: result.dnsConfigured,
                        httpsReady: result.httpsReady,
                        checkedAt: result.checkedAt,
                    }
                    : domain
            ))
        } catch (caughtError) {
            domainStatusErrors.value = {
                ...domainStatusErrors.value,
                [domainId]: caughtError instanceof Error
                    ? caughtError.message
                    : 'Não foi possível verificar o domínio.',
            }
        } finally {
            checkingDomainIds.value = checkingDomainIds.value.filter((id) => id !== domainId)
        }
    }

    const checkDomains = async () => {
        const customDomains = domains.value.filter((domain) => domain.automatic != 1)
        await Promise.all(customDomains.map((domain) => checkDomain(domain.id)))
    }

    const loadDomains = async () => {
        loadingDomains.value = true
        domainsError.value = null
        let domainsLoaded = false

        try {
            const result = await fetchBetaDomains(Number(siteStore.id))
            domains.value = result.domains
            domainDnsTarget.value = result.dnsTarget
            domainStatusErrors.value = {}
            domainsLoaded = true
        } catch (caughtError) {
            domainsError.value = caughtError instanceof Error
                ? caughtError.message
                : 'Não foi possível carregar os domínios.'
        } finally {
            loadingDomains.value = false
        }

        if (domainsLoaded) checkDomains().catch(() => undefined)
    }

    const loadReport = async () => {
        loadingReport.value = true
        reportError.value = null

        try {
            report.value = await fetchBetaReport(Number(siteStore.id))
        } catch (caughtError) {
            reportError.value = caughtError instanceof Error
                ? caughtError.message
                : 'Não foi possível carregar o relatório.'
        } finally {
            loadingReport.value = false
        }
    }

    let accessRequestId = 0

    const loadAccessCount = async () => {
        const requestId = ++accessRequestId
        loadingAccessCount.value = true
        accessCountError.value = null

        try {
            const result = await fetchBetaAccessCount(
                Number(siteStore.id),
                accessPeriod.value,
                accessPage.value,
            )
            if (requestId === accessRequestId) {
                accessCount.value = result
                accessPage.value = result.pagination.page
            }
        } catch (caughtError) {
            if (requestId === accessRequestId) {
                accessCountError.value = caughtError instanceof Error
                    ? caughtError.message
                    : 'Não foi possível carregar a contagem de acessos.'
            }
        } finally {
            if (requestId === accessRequestId) loadingAccessCount.value = false
        }
    }

    const setAccessPeriod = async (days: BetaAccessPeriod) => {
        if (![1, 15, 30].includes(days)) return
        accessPeriod.value = days
        accessPage.value = 1
        await Promise.all([
            loadAccessCount(),
            loadOpportunitiesCount(),
        ])
    }

    const setAccessPage = async (page: number) => {
        const lastPage = accessCount.value?.pagination.lastPage ?? 1
        const normalizedPage = Math.max(1, Math.min(Math.trunc(page), lastPage))
        if (normalizedPage === accessPage.value) return
        accessPage.value = normalizedPage
        await loadAccessCount()
    }

    const loadRecentPosts = async () => {
        loadingRecentPosts.value = true
        recentPostsError.value = null

        try {
            const posts = await fetchBetaPosts(Number(siteStore.id))
            recentPosts.value = posts.slice(0, 3)
        } catch (caughtError) {
            recentPosts.value = []
            recentPostsError.value = caughtError instanceof Error
                ? caughtError.message
                : 'Não foi possível carregar os últimos posts.'
        } finally {
            loadingRecentPosts.value = false
        }
    }

    const loadAdvertisements = async () => {
        loadingAdvertisements.value = true
        advertisementsError.value = null

        try {
            const result = await fetchBetaAdvertisements(Number(siteStore.id))
            advertisements.value = result.advertisements
            advertisementTitles.value = result.titles
        } catch (caughtError) {
            advertisements.value = []
            advertisementsError.value = caughtError instanceof Error
                ? caughtError.message
                : 'Não foi possível carregar as publicidades.'
        } finally {
            loadingAdvertisements.value = false
        }
    }

    const loadLinkBuilding = async () => {
        loadingLinkBuilding.value = true
        linkBuildingError.value = null

        try {
            const result = await fetchBetaLinkBuilding(Number(siteStore.id))
            linkBuildingPage.value = result.page
            linkBuildingLinks.value = result.links
        } catch (caughtError) {
            linkBuildingPage.value = null
            linkBuildingLinks.value = []
            linkBuildingError.value = caughtError instanceof Error
                ? caughtError.message
                : 'Não foi possível carregar o Link Building.'
        } finally {
            loadingLinkBuilding.value = false
        }
    }

    const loadRedirects = async () => {
        loadingRedirects.value = true
        redirectsError.value = null

        try {
            redirects.value = await fetchBetaRedirects(Number(siteStore.id))
        } catch (caughtError) {
            redirects.value = []
            redirectsError.value = caughtError instanceof Error
                ? caughtError.message
                : 'Não foi possível carregar os redirecionamentos.'
        } finally {
            loadingRedirects.value = false
        }
    }

    const loadFiles = async () => {
        loadingFiles.value = true
        filesError.value = null

        try {
            files.value = await fetchBetaFiles(Number(siteStore.id))
        } catch (caughtError) {
            files.value = []
            filesError.value = caughtError instanceof Error
                ? caughtError.message
                : 'Não foi possível carregar os arquivos.'
        } finally {
            loadingFiles.value = false
        }
    }

    const loadIntegrations = async () => {
        loadingIntegrations.value = true
        integrationsError.value = null

        try {
            const [codes, profiles, links] = await Promise.all([
                fetchBetaIntegrationCodes(Number(siteStore.id)),
                fetchBetaConnectedProfiles(Number(siteStore.id)),
                fetchBetaSocialLinks(Number(siteStore.id)),
            ])
            integrationCodes.value = codes
            connectedProfiles.value = profiles
            socialLinks.value = links
        } catch (caughtError) {
            integrationsError.value = caughtError instanceof Error
                ? caughtError.message
                : 'Não foi possível carregar as integrações.'
        } finally {
            loadingIntegrations.value = false
        }
    }

    let opportunitiesCountRequestId = 0

    const loadOpportunitiesCount = async () => {
        const requestId = ++opportunitiesCountRequestId
        loadingOpportunitiesCount.value = true
        opportunitiesCountError.value = null

        try {
            const result = await fetchBetaOpportunitiesCount(
                Number(siteStore.id),
                accessPeriod.value,
            )
            if (requestId === opportunitiesCountRequestId) {
                opportunitiesCount.value = result
            }
        } catch (caughtError) {
            if (requestId === opportunitiesCountRequestId) {
                opportunitiesCountError.value = caughtError instanceof Error
                    ? caughtError.message
                    : 'Não foi possível carregar a contagem de oportunidades.'
            }
        } finally {
            if (requestId === opportunitiesCountRequestId) {
                loadingOpportunitiesCount.value = false
            }
        }
    }

    const loadOpportunities = async () => {
        loadingOpportunities.value = true
        opportunitiesError.value = null

        try {
            opportunities.value = await fetchBetaOpportunities(Number(siteStore.id))
        } catch (caughtError) {
            opportunities.value = []
            opportunitiesError.value = caughtError instanceof Error
                ? caughtError.message
                : 'Não foi possível carregar as oportunidades.'
        } finally {
            loadingOpportunities.value = false
        }
    }

    const loadSubscriptions = async () => {
        loadingSubscriptions.value = true
        subscriptionsError.value = null

        try {
            subscriptions.value = await fetchBetaSubscriptions(Number(siteStore.id))
        } catch (caughtError) {
            subscriptions.value = {
                contracts: [],
                bills: [],
                payMethod: null,
            }
            subscriptionsError.value = caughtError instanceof Error
                ? caughtError.message
                : 'Não foi possível carregar as assinaturas.'
        } finally {
            loadingSubscriptions.value = false
        }
    }

    const loadLayouts = async () => {
        loadingLayouts.value = true
        layoutsError.value = null
        loadLayoutRollback()

        try {
            const [availableTemplates, currentLayout] = await Promise.all([
                fetchBetaTemplates(),
                fetchBetaActiveLayout(Number(siteStore.id)),
            ])

            templates.value = availableTemplates
            activeLayout.value = currentLayout

            if (layoutRollback.value
                && layoutRollback.value.appliedLayoutId !== currentLayout.layoutId) {
                persistLayoutRollback(null)
            }
        } catch (caughtError) {
            layoutsError.value = caughtError instanceof Error
                ? caughtError.message
                : 'Não foi possível carregar os layouts.'
        } finally {
            loadingLayouts.value = false
        }
    }

    const refreshActiveLayout = async () => {
        try {
            activeLayout.value = await fetchBetaActiveLayout(Number(siteStore.id))
            layoutsError.value = null
        } catch (caughtError) {
            layoutsError.value = caughtError instanceof Error
                ? caughtError.message
                : 'O layout foi aplicado, mas não pôde ser recarregado.'
        }
    }

    const applyTemplate = async (template: BetaTemplate, previewToken?: string) => {
        if (applyingLayout.value || activeLayout.value?.templateId === template.id) return

        applyingLayout.value = true
        layoutsError.value = null

        try {
            const result = await applyBetaTemplate(
                Number(siteStore.id),
                Number(template.id),
                previewToken,
            )

            persistLayoutRollback(result.rollback)

            activeLayout.value = {
                previewAvailable: activeLayout.value?.previewAvailable,
                layoutId: result.layoutId,
                templateId: result.templateId,
                templateName: template.description || 'Layout selecionado',
                data: template.dataDecoded,
            }

            await Promise.all([
                refreshActiveLayout(),
                loadPagesAndProgress(),
            ])
        } finally {
            applyingLayout.value = false
        }
    }

    const undoTemplate = async () => {
        if (applyingLayout.value || !layoutRollback.value) return

        const rollback = layoutRollback.value

        applyingLayout.value = true
        layoutsError.value = null

        try {
            const result = await undoBetaTemplate(
                Number(siteStore.id),
                rollback.token,
            )

            persistLayoutRollback(null)

            if (activeLayout.value) {
                activeLayout.value = {
                    ...activeLayout.value,
                    layoutId: result.layoutId,
                    templateId: result.templateId,
                    templateName: rollback.templateName,
                }
            }

            await Promise.all([
                refreshActiveLayout(),
                loadPagesAndProgress(),
            ])
        } finally {
            applyingLayout.value = false
        }
    }

    const analyzeSite = async (refresh = false) => {
        if (loadingAnalysis.value) return analysis.value

        loadingAnalysis.value = true
        analysisError.value = null
        persistAnalysisConsent()

        try {
            analysis.value = await fetchBetaSiteAnalysis(Number(siteStore.id), refresh)
            return analysis.value
        } catch (caughtError) {
            analysisError.value = caughtError instanceof Error
                ? caughtError.message
                : 'Não foi possível analisar o site agora.'
            return null
        } finally {
            loadingAnalysis.value = false
        }
    }

    const load = async () => {
        loading.value = true
        error.value = null
        loadManualState()
        loadAnalysisConsent()

        await Promise.all([
            loadPagesAndProgress(),
            loadDomains(),
            loadOpportunitiesCount(),
            loadAccessCount(),
            loadRecentPosts(),
        ])

        loading.value = false

        if (analysisConsent.value && !analysis.value) {
            void analyzeSite(false)
        }
    }

    const fetchPostsForPage = async (pageId: number, force = false) => {
        if (!force && postsByPage.value[pageId]) {
            return postsByPage.value[pageId]
        }

        const posts = await fetchBetaPosts(Number(siteStore.id), pageId)
        postsByPage.value[pageId] = posts

        return posts
    }

    const reorderPages = async (orderedPages: BetaPage[]) => {
        if (reorderingPages.value) return false

        const previousPages = pages.value
        const pagesById = new Map(previousPages.map((page) => [page.id, page]))
        const orderedIds = orderedPages.map((page) => page.id)

        // Only change positions: keep every page and its current data.
        if (
            orderedIds.length !== previousPages.length
            || new Set(orderedIds).size !== previousPages.length
            || orderedIds.some((id) => !pagesById.has(id))
        ) return false

        const homeId = homePage.value?.id
        const nextPages = [
            ...previousPages.filter((page) => page.id === homeId),
            ...orderedIds.filter((id) => id !== homeId).map((id) => pagesById.get(id)!),
        ]

        if (nextPages.every((page, index) => page.id === previousPages[index].id)) return true

        reorderingPages.value = true
        pages.value = nextPages

        try {
            await sortBetaPages(Number(siteStore.id), nextPages)
            notification.simple('Ordem das páginas atualizada com sucesso!', 'success')
            return true
        } catch {
            pages.value = previousPages
            notification.simple('Erro ao atualizar ordem das páginas!', 'error')
            return false
        } finally {
            reorderingPages.value = false
        }
    }

    const createPage = async (input: BetaCreatePageInput) => {
        const result = await createBetaPage(Number(siteStore.id), pages.value, input)
        pages.value = result.pages

        return result.page
    }

    const createPost = async (pageId: number) => {
        const postId = await createBetaPost(Number(siteStore.id), pageId)
        await Promise.all([
            fetchPostsForPage(pageId, true),
            loadRecentPosts(),
        ])
        portfolioHasContent.value = Object.values(postsByPage.value).some((posts) => (
            posts.some((post) => post.sketch != 1 && post.visible !== 0)
        ))

        return postId
    }

    const updatePage = async (page: BetaPage) => {
        await updateBetaPage(Number(siteStore.id), page)

        const pageIndex = pages.value.findIndex((item) => item.id === page.id)
        if (pageIndex >= 0) {
            const updatedPage = {
                ...page,
                visible: Number(Boolean(page.visible)),
                menu_visible: Number(Boolean(page.menu_visible)),
                show_opportunities_button: Number(Boolean(page.show_opportunities_button)),
            }
            pages.value.splice(pageIndex, 1, JSON.parse(JSON.stringify(updatedPage)))
        }
    }

    const duplicatePage = async (pageId: number) => {
        await duplicateBetaPage(Number(siteStore.id), pageId)
        await loadPagesAndProgress()
    }

    const deletePage = async (pageId: number) => {
        await deleteBetaPage(Number(siteStore.id), pageId)
        await loadPagesAndProgress()
    }

    const addAdvertisement = async (advertisement: BetaAdvertisementInput) => {
        const created = await createBetaAdvertisement(Number(siteStore.id), advertisement)
        advertisements.value = [...advertisements.value, created]

        return created
    }

    const editAdvertisement = async (advertisement: BetaAdvertisement) => {
        const updated = await updateBetaAdvertisement(Number(siteStore.id), advertisement)
        advertisements.value = advertisements.value.map((item) => (
            item.id === updated.id ? updated : item
        ))

        return updated
    }

    const setAdvertisementStatus = async (advertisement: BetaAdvertisement, status: boolean) => {
        return editAdvertisement({ ...advertisement, status })
    }

    const removeAdvertisement = async (advertisementId: number) => {
        await deleteBetaAdvertisement(Number(siteStore.id), advertisementId)
        advertisements.value = advertisements.value.filter((item) => item.id !== advertisementId)
    }

    const saveAdvertisementTitles = async (titles: BetaAdvertisementTitles) => {
        await updateBetaAdvertisementTitles(Number(siteStore.id), titles)
        advertisementTitles.value = JSON.parse(JSON.stringify(titles))
    }

    const saveLinkBuildingPage = async (page: BetaLinkBuildingPage) => {
        await updateBetaLinkBuildingPage(Number(siteStore.id), page)
        linkBuildingPage.value = JSON.parse(JSON.stringify(page))
    }

    const addLinkBuildingLink = async (link: BetaLinkBuildingLinkInput) => {
        const created = await createBetaLinkBuildingLink(Number(siteStore.id), link)
        linkBuildingLinks.value = [...linkBuildingLinks.value, created]

        return created
    }

    const editLinkBuildingLink = async (link: BetaLinkBuildingLink) => {
        const updated = await updateBetaLinkBuildingLink(Number(siteStore.id), link)
        linkBuildingLinks.value = linkBuildingLinks.value.map((item) => (
            item.id === updated.id ? updated : item
        ))

        return updated
    }

    const saveLinkBuildingOrder = async () => {
        try {
            await sortBetaLinkBuildingLinks(Number(siteStore.id), linkBuildingLinks.value)
        } catch (caughtError) {
            await loadLinkBuilding()
            throw caughtError
        }
    }

    const removeLinkBuildingLink = async (linkId: number) => {
        await deleteBetaLinkBuildingLink(Number(siteStore.id), linkId)
        linkBuildingLinks.value = linkBuildingLinks.value.filter((item) => item.id !== linkId)
    }

    const updateRedirects = async (items: BetaRedirect[]) => {
        redirects.value = await saveBetaRedirects(Number(siteStore.id), items)

        return redirects.value
    }

    const uploadFile = async (file: File) => {
        return uploadBetaFile(Number(siteStore.id), file)
    }

    const addFile = async (file: BetaFileInput) => {
        await createBetaFile(Number(siteStore.id), file)
        await loadFiles()
    }

    const removeFile = async (fileId: number) => {
        await deleteBetaFile(Number(siteStore.id), fileId)
        files.value = files.value.filter((file) => file.id !== fileId)
    }

    const saveIntegrationCodes = async (codes: BetaIntegrationCodes) => {
        await updateBetaIntegrationCodes(Number(siteStore.id), codes)
        integrationCodes.value = { ...codes }
    }

    const addSocialLink = async (link: BetaSocialLinkInput) => {
        await createBetaSocialLink(Number(siteStore.id), link)
        socialLinks.value = await fetchBetaSocialLinks(Number(siteStore.id))
    }

    const editSocialLink = async (link: BetaSocialLinkInput) => {
        await updateBetaSocialLink(Number(siteStore.id), link)
        socialLinks.value = await fetchBetaSocialLinks(Number(siteStore.id))
    }

    const removeSocialLink = async (linkId: number) => {
        await deleteBetaSocialLink(Number(siteStore.id), linkId)
        socialLinks.value = socialLinks.value.filter((link) => link.id !== linkId)
    }

    const findIntegrationProfile = async (
        provider: BetaProfileIntegrationProvider,
        username: string,
    ) => findBetaIntegrationProfile(Number(siteStore.id), provider, username)

    const connectIntegrationProfile = async (
        provider: BetaProfileIntegrationProvider,
        username: string,
    ) => {
        await connectBetaIntegrationProfile(Number(siteStore.id), provider, username)
        connectedProfiles.value = await fetchBetaConnectedProfiles(Number(siteStore.id))
    }

    const disconnectIntegrationProfile = async (provider: BetaProfileIntegrationProvider) => {
        await disconnectBetaIntegrationProfile(Number(siteStore.id), provider)
        connectedProfiles.value = await fetchBetaConnectedProfiles(Number(siteStore.id))
    }

    const exportOpportunities = async () => (
        downloadBetaOpportunitiesCsv(Number(siteStore.id))
    )

    const addDomain = async (domain: string) => {
        await createBetaDomain(Number(siteStore.id), domain)
        await loadDomains()
    }

    const makeMainDomain = async (domainId: number) => {
        await setBetaMainDomain(Number(siteStore.id), domainId)
        await loadDomains()
    }

    const removeDomain = async (domainId: number) => {
        await deleteBetaDomain(Number(siteStore.id), domainId)
        await loadDomains()
    }

    const getStoreUrl = async () => {
        loadingStore.value = true

        try {
            return await openBetaStore(Number(siteStore.id))
        } finally {
            loadingStore.value = false
        }
    }

    const markReviewed = () => {
        manualState.value = {
            ...manualState.value,
            reviewed: true,
        }
        persistManualState()
    }

    const dismissDomainTask = () => {
        manualState.value = {
            ...manualState.value,
            domainDismissed: true,
        }
        persistManualState()
    }

    const restoreDomainTask = () => {
        manualState.value = {
            ...manualState.value,
            domainDismissed: false,
        }
        persistManualState()
    }

    return {
        pages,
        reorderingPages,
        reorderPages,
        domains,
        domainDnsTarget,
        checkingDomainIds,
        domainStatusErrors,
        report,
        accessCount,
        accessPeriod,
        accessPage,
        postsByPage,
        recentPosts,
        templates,
        activeLayout,
        layoutRollback,
        analysis,
        analysisConsent,
        advertisements,
        advertisementTitles,
        linkBuildingPage,
        linkBuildingLinks,
        redirects,
        files,
        integrationCodes,
        connectedProfiles,
        socialLinks,
        opportunities,
        opportunitiesCount,
        subscriptions,
        loading,
        loadingContent,
        loadingDomains,
        loadingReport,
        loadingAccessCount,
        loadingRecentPosts,
        loadingStore,
        loadingLayouts,
        applyingLayout,
        loadingAnalysis,
        loadingAdvertisements,
        loadingLinkBuilding,
        loadingRedirects,
        loadingFiles,
        loadingIntegrations,
        loadingOpportunities,
        loadingOpportunitiesCount,
        loadingSubscriptions,
        error,
        reportError,
        accessCountError,
        recentPostsError,
        domainsError,
        layoutsError,
        analysisError,
        advertisementsError,
        linkBuildingError,
        redirectsError,
        filesError,
        integrationsError,
        opportunitiesError,
        opportunitiesCountError,
        subscriptionsError,
        listPages,
        contentPages,
        homePage,
        aboutPage,
        pageAssessments,
        pagePreviews,
        contentLoaded,
        contentHasErrors,
        tasks,
        progress,
        nextTask,
        load,
        loadNavigationPages,
        loadPagesAndProgress,
        loadDomains,
        checkDomain,
        checkDomains,
        loadReport,
        loadAccessCount,
        setAccessPeriod,
        setAccessPage,
        loadRecentPosts,
        loadAdvertisements,
        loadLinkBuilding,
        loadRedirects,
        loadFiles,
        loadIntegrations,
        loadOpportunities,
        loadOpportunitiesCount,
        loadSubscriptions,
        loadLayouts,
        applyTemplate,
        undoTemplate,
        analyzeSite,
        fetchPostsForPage,
        createPage,
        createPost,
        updatePage,
        duplicatePage,
        deletePage,
        addAdvertisement,
        editAdvertisement,
        setAdvertisementStatus,
        removeAdvertisement,
        saveAdvertisementTitles,
        saveLinkBuildingPage,
        addLinkBuildingLink,
        editLinkBuildingLink,
        saveLinkBuildingOrder,
        removeLinkBuildingLink,
        updateRedirects,
        uploadFile,
        addFile,
        removeFile,
        saveIntegrationCodes,
        addSocialLink,
        editSocialLink,
        removeSocialLink,
        findIntegrationProfile,
        connectIntegrationProfile,
        disconnectIntegrationProfile,
        exportOpportunities,
        addDomain,
        makeMainDomain,
        removeDomain,
        getStoreUrl,
        markReviewed,
        dismissDomainTask,
        restoreDomainTask,
    }
})
